import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Pencil, Plus } from 'lucide-react'
import { env } from '@/lib/env'
import { formatDateTimeCompact } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { formatDiscount, formatNumber, formatUsage, humanizeEnum, truncate } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import { DISCOUNT_TYPES, PROMO_STATES, type AdminPromo, type PromoFilters } from '@/types/api'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { fetchPromoCodes } from './api'
import { PromoFormDialog } from './PromoFormDialog'
import styles from './PromoCodesPage.module.css'

const DEFAULT_FILTERS = {
  search: '',
  state: '',
  discountType: '',
}

export function PromoCodesPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [editing, setEditing] = useState<AdminPromo | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  useEffect(() => {
    document.title = `Promo codes · ${env.appName} Operations`
  }, [])

  const filters = useMemo<PromoFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      state: table.filters.state || undefined,
      discountType: table.filters.discountType || undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const promosQuery = useQuery({
    queryKey: queryKeys.promos.list(filters),
    queryFn: () => fetchPromoCodes(filters),
    placeholderData: (previous) => previous,
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (promo: AdminPromo) => {
    setEditing(promo)
    setFormOpen(true)
  }

  const columns = useMemo<ColumnDef<AdminPromo, unknown>[]>(
    () => [
      {
        id: 'code',
        header: 'Code',
        meta: { sortKey: 'code', mobileLabel: 'Code' },
        cell: ({ row }) => (
          <StackedCell
            primary={<span className={styles.code}>{row.original.code}</span>}
            secondary={truncate(row.original.description, 42, 'No description')}
          />
        ),
      },
      {
        id: 'discount',
        header: 'Discount',
        meta: { sortKey: 'discountValue', mobileLabel: 'Discount' },
        cell: ({ row }) =>
          formatDiscount(row.original.discountType, row.original.discountValue, row.original.maxDiscountAmount),
      },
      {
        id: 'window',
        header: 'Valid window',
        meta: { sortKey: 'startDate', mobileLabel: 'Valid window', hideOnMobile: true },
        cell: ({ row }) => (
          <StackedCell
            primary={formatDateTimeCompact(row.original.startDate)}
            secondary={`until ${formatDateTimeCompact(row.original.endDate, 'no end date')}`}
          />
        ),
      },
      {
        id: 'usage',
        header: 'Usage',
        meta: { sortKey: 'usedCount', mobileLabel: 'Usage' },
        cell: ({ row }) => (
          <StackedCell
            primary={formatUsage(row.original.usedCount, row.original.usageLimitTotal)}
            secondary={`max ${formatNumber(row.original.usageLimitPerUser)} per customer`}
          />
        ),
      },
      {
        id: 'state',
        header: 'State',
        meta: { mobileLabel: 'State' },
        // The badge shows the resolved state, not the raw flag: a code that is
        // switched on but out of date reads as Expired, not Active.
        cell: ({ row }) => <StatusBadge status={row.original.state} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '130px' },
        cell: ({ row }) => (
          <RowActions>
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<Pencil aria-hidden="true" />}
              onClick={() => openEdit(row.original)}
            >
              Edit
            </Button>
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminPromo>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Code', value: (row) => row.code },
      { header: 'Description', value: (row) => row.description ?? '' },
      { header: 'Discount type', value: (row) => row.discountType ?? '' },
      { header: 'Discount value', value: (row) => row.discountValue ?? '' },
      { header: 'Max discount', value: (row) => row.maxDiscountAmount ?? '' },
      { header: 'Used', value: (row) => row.usedCount ?? 0 },
      { header: 'Total limit', value: (row) => row.usageLimitTotal ?? 0 },
      { header: 'Per-customer limit', value: (row) => row.usageLimitPerUser ?? 0 },
      { header: 'Valid from', value: (row) => formatDateTimeCompact(row.startDate, '') },
      { header: 'Valid until', value: (row) => formatDateTimeCompact(row.endDate, '') },
      { header: 'Enabled', value: (row) => (row.isActive ? 'Yes' : 'No') },
      { header: 'State', value: (row) => row.state },
    ],
    [],
  )

  const page = promosQuery.data

  return (
    <>
      <PageHeader
        title="Promo codes"
        description="Discounts customers can apply in the app. A code is live only when it is enabled and inside its date window — the state column shows the result of both."
        actions={
          <Button variant="primary" iconLeft={<Plus aria-hidden="true" />} onClick={openCreate}>
            New promo code
          </Button>
        }
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search code or description…"
              label="Search promo codes"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-promo-codes"
              title="Drive For Me — Promo codes"
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchPromoCodes({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="State"
            value={table.filters.state}
            onChange={(value) => table.setFilter('state', value)}
            options={PROMO_STATES.map((state) => ({ value: state, label: humanizeEnum(state) }))}
          />
          <FilterSelect
            label="Type"
            value={table.filters.discountType}
            onChange={(value) => table.setFilter('discountType', value)}
            options={DISCOUNT_TYPES.map((type) => ({ value: type, label: humanizeEnum(type) }))}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me promo codes"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={promosQuery.isLoading}
          isFetching={promosQuery.isFetching && !promosQuery.isLoading}
          error={promosQuery.isError ? promosQuery.error : undefined}
          onRetry={() => void promosQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No promo codes match these filters' : 'No promo codes yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try a different state or clear the filters.'
              : 'Create a code to run your first campaign.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : (
              <Button variant="primary" iconLeft={<Plus aria-hidden="true" />} onClick={openCreate}>
                New promo code
              </Button>
            )
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="promo code"
            disabled={promosQuery.isFetching}
          />
        ) : null}
      </Card>

      <PromoFormDialog open={formOpen} promo={editing} onClose={() => setFormOpen(false)} />
    </>
  )
}
